import IErrorInfo from "../models/account/IErrorInfo";
import { FileType } from "../models/files/FileType";
import IFileDownloadResult from "../models/files/IFileDownloadResult";
import IFileMetadata from "../models/files/IFileMetadata";
import HttpProvider from "../providers/HttpProvider";
import IUploadApiResponse from "../models/files/IUploadApiResponse";

export default class FileApi {
    public static async GetFileMetadata(
        entityId: string,
        type: FileType
    ): Promise<IFileMetadata[]> {
        const uriStr = `${this.apiUrl}/get-file-metadata`;
        const queryParams = { entityId, type };

        const response = await HttpProvider.GetRequest(uriStr, queryParams);
        return (await response.json()) as IFileMetadata[];
    }

    public static GetFileUrl(entityId: string, type: FileType, name: string) {
        const uriStr = `${this.apiUrl}/download-file`;
        const queryParams = { entityId, type, name };

        return HttpProvider.GetUrl(uriStr, queryParams);
    }

    public static async GetParcelBidsLogCsvFile(
        parcelId: string,
        auctionName: string,
        parcelName: string
    ): Promise<IFileDownloadResult> {
        const uriStr = `${this.apiUrl}/get-parcel-bids-log-csv-file`;
        const queryParams = { parcelId, auctionName, parcelName };
        const response = await HttpProvider.GetRequest(uriStr, queryParams);
        const result = {
            blob: await response.blob(),
            contentType: response.headers.get("Content-Type"),
            fileName: this.retrieveFilenameFromHeader(
                response.headers.get("Content-Disposition") || ""
            )
        } as IFileDownloadResult;
        return result;
    }

    public static async GetBiddersCsvFile(): Promise<IFileDownloadResult> {
        const uriStr = `${this.apiUrl}/get-bidders-csv-file`;
        const response = await HttpProvider.GetRequest(uriStr);
        const result = {
            blob: await response.blob(),
            contentType: response.headers.get("Content-Type"),
            fileName: this.retrieveFilenameFromHeader(
                response.headers.get("Content-Disposition") || ""
            )
        } as IFileDownloadResult;
        return result;
    }

    public static async DeleteFile(
        entityId: string,
        type: FileType,
        name: string
    ): Promise<IErrorInfo[]> {
        const uriStr = `${this.apiUrl}/delete-file`;
        const queryParams = { entityId, type, name };

        const response = await HttpProvider.DeleteRequest(uriStr, queryParams);
        if (!response.ok) {
            return (await response.json()) as IErrorInfo[];
        }

        return [];
    }

    public static async DownloadFile(
        entityId: string,
        type: FileType,
        name: string
    ): Promise<IFileDownloadResult> {
        const uriStr = `${this.apiUrl}/download-file`;
        const queryParams = { entityId, type, name };

        const response = await HttpProvider.GetRequest(uriStr, queryParams);
        const result = {
            blob: await response.blob(),
            contentType: response.headers.get("Content-Type"),
            fileName: this.retrieveFilenameFromHeader(
                response.headers.get("Content-Disposition") || ""
            )
        } as IFileDownloadResult;

        return result;
    }

    public static async ReorderFile(
        entityId: string,
        type: FileType,
        name: string,
        newIndex: number
    ): Promise<IErrorInfo[]> {
        const uriStr = `${this.apiUrl}/reorder-file`;
        const queryParams = { entityId, type, name, newIndex };

        const response = await HttpProvider.PatchRequest(uriStr, queryParams);
        if (!response.ok) {
            return (await response.json()) as IErrorInfo[];
        }

        return [];
    }

    public static async UploadFile(
        entityId: string,
        fileType: FileType,
        files: File[]
    ): Promise<IUploadApiResponse> {
        const uriStr = `${this.apiUrl}/upload-files`;

        const formData = new FormData();
        formData.append("entityId", entityId);
        formData.append("fileType", fileType.toString());

        for (const file of files) {
            formData.append("files", file);
        }

        const response = await HttpProvider.PostFormRequest(uriStr, formData);
        if (!response.ok) {
            return {
                ErrorInfo: (await response.json()) as IErrorInfo[],
                FileMetadata: []
            } as IUploadApiResponse;
        }

        return {
            ErrorInfo: [],
            FileMetadata: (await response.json()) as IFileMetadata[]
        } as IUploadApiResponse;
    }

    private static readonly apiUrl = "/api/file";

    private static retrieveFilenameFromHeader(header: string): string {
        let fileName = "";

        if (header && header.indexOf("attachment") !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(header);

            if (matches != null && matches[1]) {
                fileName = matches[1].replace(/['"]/g, "");
            }
        }

        return fileName;
    }
}
